import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Check, ArrowRight, ArrowLeft, Scissors, Plus, X, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Votre salon", "Vos services", "Vos horaires", "Termine !"];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Step 1: Shop info
  const [shopName, setShopName] = useState("");
  const [shopAddress, setShopAddress] = useState("");
  const [shopPhone, setShopPhone] = useState("");

  // Step 2: Services
  const [services, setServices] = useState([]);
  const [presets, setPresets] = useState([]);
  const [customName, setCustomName] = useState("");
  const [customDuration, setCustomDuration] = useState(30);
  const [customPrice, setCustomPrice] = useState(25);
  const [showCustom, setShowCustom] = useState(false);

  // Step 3: Hours
  const [hours, setHours] = useState({});

  useEffect(() => {
    API.get("/onboarding/presets").then(r => {
      setPresets(r.data.services);
      setHours(r.data.working_hours);
    }).catch(() => {});
  }, []);

  const togglePreset = (preset) => {
    const exists = services.find(s => s.name === preset.name);
    if (exists) {
      setServices(services.filter(s => s.name !== preset.name));
    } else {
      setServices([...services, { ...preset, active: true }]);
    }
  };

  const addCustomService = () => {
    if (!customName.trim()) return;
    setServices([...services, { name: customName, duration: customDuration, price: customPrice, active: true }]);
    setCustomName("");
    setCustomDuration(30);
    setCustomPrice(25);
    setShowCustom(false);
  };

  const removeService = (idx) => {
    setServices(services.filter((_, i) => i !== idx));
  };

  const updateHours = (day, field, value) => {
    setHours({ ...hours, [day]: { ...hours[day], [field]: value } });
  };

  const toggleDayClosed = (day) => {
    setHours({ ...hours, [day]: { ...hours[day], closed: !hours[day].closed } });
  };

  const handleNext = async () => {
    if (step === 0) {
      if (!shopName.trim()) { toast.error("Entrez le nom de votre salon"); return; }
      setLoading(true);
      try {
        await API.post("/barbershop", { name: shopName, address: shopAddress, phone: shopPhone });
        setStep(1);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur lors de la creation");
      } finally {
        setLoading(false);
      }
    } else if (step === 1) {
      if (services.length === 0) { toast.error("Ajoutez au moins un service"); return; }
      setLoading(true);
      try {
        await API.post("/services/bulk", services.map(s => ({ name: s.name, duration: s.duration, price: s.price, active: s.active })));
        setStep(2);
      } catch (err) {
        toast.error("Erreur lors de l'ajout des services");
      } finally {
        setLoading(false);
      }
    } else if (step === 2) {
      setLoading(true);
      try {
        await API.put("/barbershop", { working_hours: hours });
        await API.post("/onboarding/complete");
        await refreshUser();
        setStep(3);
      } catch (err) {
        toast.error("Erreur lors de la mise a jour");
      } finally {
        setLoading(false);
      }
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  const dayLabels = {
    lundi: "Lundi", mardi: "Mardi", mercredi: "Mercredi",
    jeudi: "Jeudi", vendredi: "Vendredi", samedi: "Samedi", dimanche: "Dimanche"
  };

  return (
    <div className="min-h-screen bg-marketing-bg flex flex-col" data-testid="onboarding-page">
      {/* Progress Header */}
      <div className="bg-white border-b border-border p-4">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Scissors size={18} className="text-primary" />
              <span className="font-heading font-bold text-sm">CutIt</span>
            </div>
            <span className="text-xs text-muted-foreground">{step + 1}/{STEPS.length}</span>
          </div>
          <Progress value={progress} className="h-1.5" data-testid="onboarding-progress" />
          <div className="flex justify-between mt-2">
            {STEPS.map((s, i) => (
              <span key={i} className={`text-[10px] ${i <= step ? "text-primary font-medium" : "text-muted-foreground"}`}>
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center p-6 pt-10">
        <div className="w-full max-w-xl">
          {/* Step 0: Shop Info */}
          {step === 0 && (
            <div className="animate-fade-up">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Parlons de votre salon</h2>
              <p className="text-muted-foreground mb-8">Ces infos seront visibles sur votre page de reservation.</p>
              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Nom du salon *</Label>
                  <Input value={shopName} onChange={e => setShopName(e.target.value)} placeholder="Ex: Le Barber Shop" className="h-12" data-testid="shop-name-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Adresse</Label>
                  <Input value={shopAddress} onChange={e => setShopAddress(e.target.value)} placeholder="12 Rue de la Paix, Paris" className="h-12" data-testid="shop-address-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Telephone</Label>
                  <Input value={shopPhone} onChange={e => setShopPhone(e.target.value)} placeholder="06 12 34 56 78" className="h-12" data-testid="shop-phone-input" />
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Services */}
          {step === 1 && (
            <div className="animate-fade-up">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Vos services</h2>
              <p className="text-muted-foreground mb-8">Selectionnez vos services ou ajoutez les votres.</p>

              {/* Presets */}
              <div className="flex flex-wrap gap-2 mb-6">
                {presets.map((p, i) => {
                  const selected = services.find(s => s.name === p.name);
                  return (
                    <button
                      key={i}
                      onClick={() => togglePreset(p)}
                      className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all ${selected ? "bg-primary text-white border-primary" : "bg-white border-border text-foreground hover:border-primary/30"}`}
                      data-testid={`preset-${i}`}
                    >
                      {selected && <Check size={14} className="inline mr-1.5" />}
                      {p.name} - {p.price}EUR
                    </button>
                  );
                })}
              </div>

              {/* Selected services */}
              {services.length > 0 && (
                <div className="space-y-2 mb-6">
                  {services.map((s, i) => (
                    <div key={i} className="flex items-center justify-between bg-white border border-border rounded-xl px-4 py-3">
                      <div>
                        <p className="text-sm font-medium">{s.name}</p>
                        <p className="text-xs text-muted-foreground">{s.duration} min - {s.price}EUR</p>
                      </div>
                      <button onClick={() => removeService(i)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-service-${i}`}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Custom service */}
              {showCustom ? (
                <div className="bg-white border border-border rounded-xl p-4 space-y-3">
                  <Input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="Nom du service" className="h-10" data-testid="custom-service-name" />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs mb-1 block">Duree (min)</Label>
                      <Input type="number" value={customDuration} onChange={e => setCustomDuration(parseInt(e.target.value) || 0)} className="h-10" data-testid="custom-service-duration" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">Prix (EUR)</Label>
                      <Input type="number" value={customPrice} onChange={e => setCustomPrice(parseFloat(e.target.value) || 0)} className="h-10" data-testid="custom-service-price" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addCustomService} className="rounded-full bg-primary hover:bg-blue-700" data-testid="add-custom-service-btn">Ajouter</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowCustom(false)}>Annuler</Button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowCustom(true)} className="flex items-center gap-2 text-sm text-primary font-medium hover:underline" data-testid="show-custom-service">
                  <Plus size={16} /> Ajouter un service personnalise
                </button>
              )}
            </div>
          )}

          {/* Step 2: Hours */}
          {step === 2 && (
            <div className="animate-fade-up">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Vos horaires</h2>
              <p className="text-muted-foreground mb-8">Definissez vos heures d'ouverture. Vous pourrez les modifier plus tard.</p>
              <div className="space-y-3">
                {Object.entries(hours).map(([day, h]) => (
                  <div key={day} className="flex items-center gap-4 bg-white border border-border rounded-xl px-4 py-3">
                    <div className="w-24">
                      <p className={`text-sm font-medium ${h.closed ? "text-muted-foreground line-through" : ""}`}>
                        {dayLabels[day] || day}
                      </p>
                    </div>
                    <Switch checked={!h.closed} onCheckedChange={() => toggleDayClosed(day)} data-testid={`day-toggle-${day}`} />
                    {!h.closed && (
                      <div className="flex items-center gap-2 text-sm">
                        <input
                          type="time"
                          value={h.open}
                          onChange={e => updateHours(day, "open", e.target.value)}
                          className="border border-border rounded-md px-2 py-1 text-sm"
                          data-testid={`hours-open-${day}`}
                        />
                        <span className="text-muted-foreground">a</span>
                        <input
                          type="time"
                          value={h.close}
                          onChange={e => updateHours(day, "close", e.target.value)}
                          className="border border-border rounded-md px-2 py-1 text-sm"
                          data-testid={`hours-close-${day}`}
                        />
                      </div>
                    )}
                    {h.closed && <span className="text-xs text-muted-foreground">Ferme</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <div className="text-center animate-scale-in">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <PartyPopper size={36} className="text-green-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">Votre salon est pret !</h2>
              <p className="text-muted-foreground mb-10 max-w-md mx-auto">
                Votre page de reservation est en ligne. Partagez-la avec vos clients et commencez a recevoir des reservations.
              </p>
              <Button
                onClick={() => navigate("/tableau-de-bord")}
                className="rounded-full px-10 py-6 text-base font-semibold bg-primary hover:bg-blue-700"
                data-testid="go-to-dashboard-btn"
              >
                Acceder au tableau de bord
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          )}

          {/* Navigation */}
          {step < 3 && (
            <div className="flex items-center justify-between mt-10">
              {step > 0 ? (
                <Button variant="ghost" onClick={() => setStep(step - 1)} className="gap-2" data-testid="onboarding-back-btn">
                  <ArrowLeft size={16} /> Retour
                </Button>
              ) : <div />}
              <Button
                onClick={handleNext}
                disabled={loading}
                className="rounded-full px-8 py-5 bg-primary hover:bg-blue-700 font-semibold gap-2"
                data-testid="onboarding-next-btn"
              >
                {loading ? "Chargement..." : "Continuer"}
                <ArrowRight size={16} />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
